import {
  ClerkLoaded,
  ClerkLoading,
  RedirectToSignIn,
  SignedIn,
  SignedOut,
  useClerk,
  useUser,
} from '@clerk/clerk-react';
import type React from 'react';
import { Suspense, lazy, useCallback, useEffect, useMemo, useState } from 'react';
import { Provider } from 'react-redux';
import { BrowserRouter, Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import {
  CheckIcon,
  DiamondIcon,
  DocumentIcon,
  LocationIcon,
  StarIcon,
  UserIcon,
  WrenchIcon,
} from './Icons';
import { Layout } from './Layout';
import { ClerkAuthSync } from './components/ClerkAuthSync';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ToastContainer } from './components/ui/ToastContainer';
import {
  Button,
  Card,
  DashboardWidget,
  EmptyState,
  Input,
  LoadingSpinner,
  MapView,
  Modal,
  NotificationItem,
  RequestCard,
  Select,
  StatusBadge,
  TextArea,
} from './ezhoaComponents';
import { useAppDispatch, useAppSelector } from './hooks';
// Redux store and actions
import { store } from './store/index';
import { adminActions } from './store/slices/adminSlice';
import {
  resetBidForm,
  resetRequestForm,
  updateBidForm,
  updateRequestForm,
} from './store/slices/formsSlice';
import { selectAllNotifications } from './store/slices/notificationsSlice';
import { selectAllRequests } from './store/slices/requestsSlice';
import { selectRequest, setActiveModal, showToast, toggleMapView } from './store/slices/uiSlice';
import type { Plumber } from './types';
import 'leaflet/dist/leaflet.css';

// Lazy load heavy route components to reduce initial bundle size
const JaredAIAgent = lazy(() =>
  import('./components/JaredAIAgent').then((m) => ({ default: m.JaredAIAgent }))
);
const JaredDocumentsPage = lazy(() =>
  import('./components/JaredDocuments').then((m) => ({ default: m.JaredDocumentsPage }))
);
const JaredVendorMarketplace = lazy(() =>
  import('./components/JaredVendorMarketplace').then((m) => ({ default: m.JaredVendorMarketplace }))
);
const CalendarPage = lazy(() =>
  import('./pages/CalendarPage').then((m) => ({ default: m.CalendarPage }))
);
const StripeDemoPage = lazy(() =>
  import('./pages/StripeDemo').then((m) => ({ default: m.StripeDemoPage }))
);
const JaredChat = lazy(() =>
  import('./components/JaredChat').then((m) => ({ default: m.JaredChat }))
);
const JaredCommunityBroadcast = lazy(() =>
  import('./components/JaredCommunityBroadcast').then((m) => ({ default: m.JaredCommunityBroadcast }))
);
const JaredAdminDashboard = lazy(() =>
  import('./components/JaredDashboard').then((m) => ({ default: m.JaredAdminDashboard }))
);
const JaredHomeownerDashboard = lazy(() =>
  import('./components/JaredDashboard').then((m) => ({ default: m.JaredHomeownerDashboard }))
);
const JaredVendorDashboard = lazy(() =>
  import('./components/JaredDashboard').then((m) => ({ default: m.JaredVendorDashboard }))
);
const JaredPaymentPage = lazy(() =>
  import('./components/JaredPayment').then((m) => ({ default: m.JaredPaymentPage }))
);
const JaredPrivacySettings = lazy(() =>
  import('./components/JaredPrivacySettings').then((m) => ({ default: m.JaredPrivacySettings }))
);
const JaredRequestsPage = lazy(() =>
  import('./components/JaredRequests').then((m) => ({ default: m.JaredRequestsPage }))
);
const JaredVendorAssignment = lazy(() =>
  import('./components/JaredVendorAssignment').then((m) => ({ default: m.JaredVendorAssignment }))
);
const ClerkLogin = lazy(() =>
  import('./pages/ClerkLogin').then((m) => ({ default: m.default }))
);
const ClerkSignUp = lazy(() =>
  import('./pages/ClerkSignUp').then((m) => ({ default: m.default }))
);

// Dashboard Pages - Using Jared's versions

// Requests Page (lazy-loaded)
const RequestsPage: React.FC = () => {
  return (
    <Suspense
      fallback={
        <div className="flex items-center justify-center min-h-[400px]">
          <LoadingSpinner />
        </div>
      }
    >
      <JaredRequestsPage />
    </Suspense>
  );
};

// Jobs Page (Vendor)
const JobsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const showMap = useAppSelector((state) => state.ui?.showMapView || false);
  const selectedVendorId = useAppSelector((state) => state.vendors?.selectedVendorIds?.[0] || null);

  const requests = useAppSelector(selectAllRequests);
  const vendors = useAppSelector((state) => state.vendors?.vendors || []);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Available Jobs</h1>
        <Button variant="outline" onClick={() => dispatch(toggleMapView())} icon={<LocationIcon />}>
          {showMap ? 'List View' : 'Map View'}
        </Button>
      </div>

      {showMap ? (
        <Card className="h-[600px] overflow-hidden">
          <MapView
            vendors={vendors}
            selectedVendor={selectedVendorId || undefined}
            onVendorSelect={(_id) => {
              // TODO: Implement vendor selection
            }}
          />
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {requests.map((request) => (
            <RequestCard
              key={request.id}
              request={request}
              onSelect={() => {
                dispatch(selectRequest(request.id));
                dispatch(setActiveModal('submitBid'));
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Messages Page (lazy-loaded)
const MessagesPage: React.FC = () => {
  return (
    <Suspense
      fallback={
        <div className="flex items-center justify-center min-h-[400px]">
          <LoadingSpinner />
        </div>
      }
    >
      <JaredChat />
    </Suspense>
  );
};

// Notifications Page
const NotificationsPage: React.FC = () => {
  const notifications = useAppSelector(selectAllNotifications);

  return (
    <div className="max-w-2xl mx-auto">
      <Card>
        <div className="p-4 border-b">
          <h1 className="text-xl font-semibold">Notifications</h1>
        </div>
        <div className="divide-y">
          {notifications.map((notification) => (
            <NotificationItem
              key={notification.id}
              notification={{
                id: notification.id,
                userId: notification.userId,
                type:
                  notification.type === 'info' ||
                  notification.type === 'success' ||
                  notification.type === 'warning' ||
                  notification.type === 'error'
                    ? 'system'
                    : notification.type,
                title: notification.title,
                content: notification.message,
                read: notification.read,
                actionUrl: notification.actionUrl,
                createdAt: notification.timestamp,
              }}
            />
          ))}
        </div>
      </Card>
    </div>
  );
};

// Documents Page
const DocumentsPage: React.FC = () => {
  return <JaredDocumentsPage />;
};

// Calendar Page is imported from ./pages/CalendarPage

// Settings Page
const SettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const ezhoaUser = useAppSelector((state) => state.clerkAuth?.ezhoaUser);
  const { signOut } = useClerk();

  // Load preferences from localStorage
  const [emailNotifications, setEmailNotifications] = useState(() => {
    return localStorage.getItem('emailNotifications') !== 'false';
  });
  const [smsNotifications, setSmsNotifications] = useState(() => {
    return localStorage.getItem('smsNotifications') !== 'false';
  });

  const handleSignOut = () => {
    signOut({ redirectUrl: '/' });
  };

  const toggleEmailNotifications = () => {
    const newValue = !emailNotifications;
    setEmailNotifications(newValue);
    localStorage.setItem('emailNotifications', newValue.toString());
    dispatch(
      showToast({
        message: `Email notifications ${newValue ? 'enabled' : 'disabled'}`,
        type: 'success',
      })
    );
  };

  const toggleSmsNotifications = () => {
    const newValue = !smsNotifications;
    setSmsNotifications(newValue);
    localStorage.setItem('smsNotifications', newValue.toString());
    dispatch(
      showToast({
        message: `SMS notifications ${newValue ? 'enabled' : 'disabled'}`,
        type: 'success',
      })
    );
  };

  return (
    <div className="space-y-6 max-w-2xl mx-auto">
      <h1 className="text-2xl font-semibold">Settings</h1>

      <Card className="divide-y">
        <div className="p-4">
          <h2 className="text-lg font-semibold mb-4">Account Information</h2>
          <div className="space-y-3">
            <div>
              <span className="text-sm text-gray-600">Name</span>
              <p className="font-medium">{ezhoaUser?.name}</p>
            </div>
            <div>
              <span className="text-sm text-gray-600">Email</span>
              <p className="font-medium">{ezhoaUser?.email}</p>
            </div>
            <div>
              <span className="text-sm text-gray-600">Role</span>
              <p className="font-medium capitalize">{ezhoaUser?.role}</p>
            </div>
            <div>
              <span className="text-sm text-gray-600">Phone</span>
              <p className="font-medium">{ezhoaUser?.phone}</p>
            </div>
          </div>
        </div>

        <div className="p-4">
          <h2 className="text-lg font-semibold mb-4">Preferences</h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Email Notifications</p>
                <p className="text-sm text-gray-600">Receive updates via email</p>
              </div>
              <Button
                variant={emailNotifications ? 'primary' : 'outline'}
                size="sm"
                onClick={toggleEmailNotifications}
              >
                {emailNotifications ? 'Enabled' : 'Disabled'}
              </Button>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">SMS Notifications</p>
                <p className="text-sm text-gray-600">Receive text message alerts</p>
              </div>
              <Button
                variant={smsNotifications ? 'primary' : 'outline'}
                size="sm"
                onClick={toggleSmsNotifications}
              >
                {smsNotifications ? 'Enabled' : 'Disabled'}
              </Button>
            </div>
          </div>
        </div>

        <div className="p-4">
          <h2 className="text-lg font-semibold mb-4">Privacy & Security</h2>
          <div className="space-y-3">
            <Button
              variant="outline"
              className="w-full justify-start"
              onClick={() => navigate('/privacy')}
            >
              Privacy Settings
            </Button>
            <Button
              variant="outline"
              className="w-full justify-start"
              onClick={() => {
                dispatch(
                  showToast({
                    message: 'Manage password in your Clerk account settings',
                    type: 'info',
                  })
                );
              }}
            >
              Change Password
            </Button>
            <Button
              variant="outline"
              className="w-full justify-start"
              onClick={() => {
                dispatch(
                  showToast({
                    message: 'Configure 2FA in your Clerk account settings',
                    type: 'info',
                  })
                );
              }}
            >
              Two-Factor Authentication
            </Button>
          </div>
        </div>

        <div className="p-4">
          <Button
            variant="secondary"
            onClick={handleSignOut}
            className="w-full text-red-600 hover:bg-red-50"
          >
            Sign Out
          </Button>
        </div>
      </Card>
    </div>
  );
};

// Profile Page
const ProfilePage: React.FC = () => {
  const ezhoaUser = useAppSelector((state) => state.clerkAuth?.ezhoaUser);

  return (
    <div className="space-y-6 max-w-2xl mx-auto">
      <h1 className="text-2xl font-semibold">Profile</h1>

      <Card className="p-6">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-20 h-20 bg-[#1E88E5] rounded-full flex items-center justify-center">
            <span className="text-2xl font-bold text-white">
              {ezhoaUser?.name?.charAt(0).toUpperCase()}
            </span>
          </div>
          <div>
            <h2 className="text-xl font-semibold">{ezhoaUser?.name}</h2>
            <p className="text-gray-600 capitalize">{ezhoaUser?.role}</p>
            <p className="text-sm text-gray-500">{ezhoaUser?.email}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="font-semibold mb-3">Contact Information</h3>
            <div className="space-y-2">
              <div>
                <span className="text-sm text-gray-600">Phone</span>
                <p>{ezhoaUser?.phone}</p>
              </div>
              <div>
                <span className="text-sm text-gray-600">Address</span>
                <p>{ezhoaUser?.address || 'Not provided'}</p>
              </div>
            </div>
          </div>

          <div>
            <h3 className="font-semibold mb-3">HOA Information</h3>
            <div className="space-y-2">
              <div>
                <span className="text-sm text-gray-600">HOA ID</span>
                <p>{ezhoaUser?.hoaId}</p>
              </div>
              <div>
                <span className="text-sm text-gray-600">Member Since</span>
                <p>
                  {ezhoaUser?.createdAt
                    ? new Date(ezhoaUser.createdAt).toLocaleDateString()
                    : 'N/A'}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 pt-6 border-t">
          <Button variant="primary">Edit Profile</Button>
        </div>
      </Card>
    </div>
  );
};

// Create Request Modal
const CreateRequestModal: React.FC = () => {
  const dispatch = useAppDispatch();
  const activeModal = useAppSelector((state) => state.ui?.activeModal);
  const { title, description, category, priority, address } = useAppSelector(
    (state) =>
      state.forms?.requestForm || {
        title: '',
        description: '',
        category: '',
        priority: 'medium',
        address: '',
      }
  );

  const handleSubmit = () => {
    if (title && description && category && address) {
      // Create request logic would go here
      dispatch(
        showToast({
          type: 'success',
          message: 'Request created successfully!',
        })
      );
      /*dispatch(
        createRequest({
          homeownerId: user?.id || '',
          title,
          description,
          category: category as
            | 'plumbing'
            | 'landscaping'
            | 'electrical'
            | 'roofing'
            | 'maintenance'
            | 'other',
          status: 'pending',
          priority: priority as 'low' | 'medium' | 'high' | 'urgent',
          location: {
            address,
            coordinates: [34.0522, -118.2437],
          },
          images: [],
          notes: [],
        })
      );*/
      dispatch(setActiveModal(null));
      dispatch(resetRequestForm());
    }
  };

  return (
    <Modal
      isOpen={activeModal === 'createRequest'}
      onClose={() => dispatch(setActiveModal(null))}
      title="Submit Service Request"
      size="lg"
    >
      <div className="space-y-4">
        <Input
          placeholder="Request Title"
          value={title}
          onChange={(e) => dispatch(updateRequestForm({ title: e.target.value }))}
        />

        <TextArea
          placeholder="Describe the issue in detail..."
          value={description}
          onChange={(e) => dispatch(updateRequestForm({ description: e.target.value }))}
          rows={4}
        />

        <Select
          value={category}
          onChange={(value) => dispatch(updateRequestForm({ category: value }))}
          placeholder="Select Category"
          options={[
            { value: 'plumbing', label: 'Plumbing' },
            { value: 'landscaping', label: 'Landscaping' },
            { value: 'electrical', label: 'Electrical' },
            { value: 'roofing', label: 'Roofing' },
            { value: 'maintenance', label: 'Maintenance' },
            { value: 'other', label: 'Other' },
          ]}
        />

        <Select
          value={priority}
          onChange={(value) =>
            dispatch(updateRequestForm({ priority: value as 'low' | 'medium' | 'high' }))
          }
          options={[
            { value: 'low', label: 'Low Priority' },
            { value: 'medium', label: 'Medium Priority' },
            { value: 'high', label: 'High Priority' },
          ]}
        />

        <Input
          placeholder="Service Address"
          value={address}
          onChange={(e) => dispatch(updateRequestForm({ address: e.target.value }))}
          icon={<LocationIcon />}
        />

        <div className="flex gap-3 pt-4">
          <Button
            variant="secondary"
            onClick={() => dispatch(setActiveModal(null))}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit} className="flex-1">
            Submit Request
          </Button>
        </div>
      </div>
    </Modal>
  );
};

// Submit Bid Modal
const SubmitBidModal: React.FC = () => {
  const dispatch = useAppDispatch();
  const activeModal = useAppSelector((state) => state.ui?.activeModal);
  const selectedRequestId = useAppSelector((state) => state.ui?.selectedRequestId);
  const {
    amount,
    message: description,
    estimatedDuration: duration,
  } = useAppSelector(
    (state) => state.forms?.bidForm || { amount: 0, message: '', estimatedDuration: '' }
  );

  const handleSubmit = () => {
    if (amount && description && duration && selectedRequestId) {
      // Create bid logic would go here
      dispatch(
        showToast({
          type: 'success',
          message: 'Bid submitted successfully!',
        })
      );
      /*dispatch(
        createBid({
          vendorId: user?.id || '',
          requestId: selectedRequestId,
          amount: Number.parseFloat(amount),
          description,
          estimatedDuration: duration,
          status: 'pending',
        })
      );*/
      dispatch(setActiveModal(null));
      dispatch(resetBidForm());
    }
  };

  return (
    <Modal
      isOpen={activeModal === 'submitBid'}
      onClose={() => dispatch(setActiveModal(null))}
      title="Submit Bid"
      size="lg"
    >
      <div className="space-y-4">
        {/* TODO: Add request lookup when state is properly set up */}

        <Input
          type="number"
          placeholder="Bid Amount ($)"
          value={amount.toString()}
          onChange={(e) =>
            dispatch(updateBidForm({ amount: Number.parseFloat(e.target.value) || 0 }))
          }
        />

        <TextArea
          placeholder="Describe your approach and what's included..."
          value={description}
          onChange={(e) => dispatch(updateBidForm({ message: e.target.value }))}
          rows={4}
        />

        <Input
          placeholder="Estimated Duration (e.g., 2-3 days)"
          value={duration}
          onChange={(e) => dispatch(updateBidForm({ estimatedDuration: e.target.value }))}
        />

        <div className="bg-blue-50 p-4 rounded-lg">
          <p className="text-sm text-blue-800">
            Platform fee: 10% (${amount ? (amount * 0.1).toFixed(2) : '0.00'})
          </p>
          <p className="text-sm text-blue-800">
            You'll receive: ${amount ? (amount * 0.9).toFixed(2) : '0.00'}
          </p>
        </div>

        <div className="flex gap-3 pt-4">
          <Button
            variant="secondary"
            onClick={() => dispatch(setActiveModal(null))}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit} className="flex-1">
            Submit Bid
          </Button>
        </div>
      </div>
    </Modal>
  );
};

// Vendors Page for Admin Management
const VendorsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');
  const [filter, setFilter] = useState({
    status: 'all',
    category: 'all',
    approval: 'all',
  });

  const vendors = useAppSelector((state) => state.vendors?.vendors || []);
  const loading = useAppSelector((state) => state.vendors?.loading || false);
  const error = useAppSelector((state) => state.vendors?.error || null);
  const user = useAppSelector((state) => state.clerkAuth?.ezhoaUser);

  // Vendors are loaded from the database via Redux

  // Memoize event handlers to prevent recreation on every render
  const handleApproveVendor = useCallback(
    async (vendorId: string) => {
      try {
        dispatch(adminActions.approveVendor(vendorId));
        dispatch(
          showToast({
            type: 'success',
            message: 'Vendor approved successfully',
          })
        );
      } catch (_error) {
        dispatch(
          showToast({
            type: 'error',
            message: 'Failed to approve vendor',
          })
        );
      }
    },
    [dispatch]
  );

  const handleRejectVendor = useCallback(
    async (vendorId: string, reason: string) => {
      try {
        dispatch(adminActions.rejectVendor({ vendorId, reason }));
        dispatch(
          showToast({
            type: 'success',
            message: 'Vendor rejected',
          })
        );
      } catch (_error) {
        dispatch(
          showToast({
            type: 'error',
            message: 'Failed to reject vendor',
          })
        );
      }
    },
    [dispatch]
  );

  const handleSetPreferred = useCallback(
    async (vendorId: string) => {
      try {
        dispatch(adminActions.setPreferredVendor(vendorId));
        dispatch(
          showToast({
            type: 'success',
            message: 'Vendor set as preferred',
          })
        );
      } catch (_error) {
        dispatch(
          showToast({
            type: 'error',
            message: 'Failed to set preferred vendor',
          })
        );
      }
    },
    [dispatch]
  );

  // Memoize expensive filtering operation to prevent recalculation on every render
  const filteredVendors = useMemo(() => {
    return vendors.filter((vendor) => {
      if (filter.status !== 'all' && vendor.hoaApprovalStatus !== filter.status) return false;
      if (filter.category !== 'all' && vendor.category !== filter.category) return false;
      if (filter.approval !== 'all') {
        if (filter.approval === 'verified' && vendor.backgroundCheckStatus !== 'verified')
          return false;
        if (filter.approval === 'pending' && vendor.backgroundCheckStatus !== 'pending')
          return false;
      }
      return true;
    });
  }, [vendors, filter.status, filter.category, filter.approval]);

  if (user?.role !== 'admin') {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h2>
        <p className="text-gray-600">Only administrators can access vendor management.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Vendor Management</h1>
          <p className="text-gray-600 mt-1">Manage and approve vendors for your HOA</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant={viewMode === 'grid' ? 'primary' : 'outline'}
            onClick={() => setViewMode('grid')}
            icon={<DiamondIcon />}
          >
            Grid
          </Button>
          <Button
            variant={viewMode === 'table' ? 'primary' : 'outline'}
            onClick={() => setViewMode('table')}
            icon={<DocumentIcon />}
          >
            Table
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Select
            value={filter.status}
            onChange={(value) => setFilter({ ...filter, status: value })}
            options={[
              { value: 'all', label: 'All Status' },
              { value: 'pending', label: 'Pending Approval' },
              { value: 'approved', label: 'Approved' },
              { value: 'rejected', label: 'Rejected' },
            ]}
            placeholder="Filter by Status"
          />
          <Select
            value={filter.category}
            onChange={(value) => setFilter({ ...filter, category: value })}
            options={[
              { value: 'all', label: 'All Categories' },
              { value: 'plumbing', label: 'Plumbing' },
              { value: 'electrical', label: 'Electrical' },
              { value: 'landscaping', label: 'Landscaping' },
              { value: 'roofing', label: 'Roofing' },
              { value: 'maintenance', label: 'Maintenance' },
            ]}
            placeholder="Filter by Category"
          />
          <Select
            value={filter.approval}
            onChange={(value) => setFilter({ ...filter, approval: value })}
            options={[
              { value: 'all', label: 'All Verification' },
              { value: 'verified', label: 'Background Verified' },
              { value: 'pending', label: 'Verification Pending' },
            ]}
            placeholder="Filter by Verification"
          />
        </div>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <DashboardWidget
          title="Total Vendors"
          icon={<UserIcon />}
          value={vendors.length}
          color="blue"
        />
        <DashboardWidget
          title="Approved"
          icon={<CheckIcon />}
          value={vendors.filter((v) => v.hoaApprovalStatus === 'approved').length}
          color="green"
        />
        <DashboardWidget
          title="Pending"
          icon={<WrenchIcon />}
          value={vendors.filter((v) => v.hoaApprovalStatus === 'pending').length}
          color="orange"
        />
        <DashboardWidget
          title="Preferred"
          icon={<StarIcon />}
          value={vendors.filter((v) => v.contractType === 'preferred').length}
          color="purple"
        />
      </div>

      {/* Vendor List/Grid */}
      {loading ? (
        <LoadingSpinner />
      ) : error ? (
        <Card className="p-8 text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <Button onClick={() => window.location.reload()}>Retry</Button>
        </Card>
      ) : filteredVendors.length === 0 ? (
        <EmptyState
          icon={<UserIcon size={48} />}
          title="No Vendors Found"
          description="No vendors match your current filters. Try adjusting your search criteria."
        />
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredVendors.map((vendor) => (
            <VendorManagementCard
              key={vendor.id}
              vendor={vendor}
              onApprove={() => handleApproveVendor(vendor.id)}
              onReject={(reason) => handleRejectVendor(vendor.id, reason)}
              onSetPreferred={() => handleSetPreferred(vendor.id)}
            />
          ))}
        </div>
      ) : (
        <VendorManagementTable
          vendors={filteredVendors}
          onApprove={handleApproveVendor}
          onReject={handleRejectVendor}
          onSetPreferred={handleSetPreferred}
        />
      )}
    </div>
  );
};

// Vendor Management Card Component
const VendorManagementCard: React.FC<{
  vendor: Plumber;
  onApprove: () => void;
  onReject: (reason: string) => void;
  onSetPreferred: () => void;
}> = ({ vendor, onApprove, onReject, onSetPreferred }) => {
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const statusColors: Record<string, string> = {
    pending: 'bg-yellow-100 text-yellow-800',
    approved: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800',
  };

  const contractColors: Record<string, string> = {
    trial: 'bg-gray-100 text-gray-800',
    standard: 'bg-blue-100 text-blue-800',
    preferred: 'bg-purple-100 text-purple-800',
  };

  return (
    <>
      <Card className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-[#E3F2FD] rounded-full flex items-center justify-center">
              <WrenchIcon className="text-[#1E88E5]" size={20} />
            </div>
            <div>
              <h3 className="font-semibold text-lg">{vendor.name}</h3>
              <p className="text-sm text-gray-600 capitalize">{vendor.category}</p>
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <span
              className={`px-2 py-1 rounded-full text-xs ${statusColors[vendor.hoaApprovalStatus || 'pending']}`}
            >
              {vendor.hoaApprovalStatus || 'pending'}
            </span>
            {vendor.contractType && (
              <span
                className={`px-2 py-1 rounded-full text-xs ${contractColors[vendor.contractType]}`}
              >
                {vendor.contractType}
              </span>
            )}
          </div>
        </div>

        <div className="space-y-2 mb-4">
          <div className="flex items-center gap-2">
            <StarIcon size={16} className="text-yellow-400" />
            <span className="text-sm">
              {vendor.rating} ({vendor.reviewCount} reviews)
            </span>
          </div>
          <div className="flex items-center gap-2">
            <LocationIcon size={16} className="text-gray-400" />
            <span className="text-sm">{vendor.location}</span>
          </div>
          {vendor.licenseNumber && (
            <div className="flex items-center gap-2">
              <DocumentIcon size={16} className="text-gray-400" />
              <span className="text-sm">License: {vendor.licenseNumber}</span>
            </div>
          )}
          {vendor.insurance && (
            <div className="flex items-center gap-2">
              <CheckIcon size={16} className="text-green-600" />
              <span className="text-sm">Insured</span>
            </div>
          )}
        </div>

        <div className="flex gap-2">
          {vendor.hoaApprovalStatus === 'pending' && (
            <>
              <Button variant="primary" size="sm" onClick={onApprove} className="flex-1">
                Approve
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowRejectModal(true)}
                className="flex-1"
              >
                Reject
              </Button>
            </>
          )}
          {vendor.hoaApprovalStatus === 'approved' && vendor.contractType !== 'preferred' && (
            <Button
              variant="outline"
              size="sm"
              onClick={onSetPreferred}
              icon={<StarIcon />}
              className="w-full"
            >
              Set as Preferred
            </Button>
          )}
        </div>
      </Card>

      {/* Reject Modal */}
      <Modal
        isOpen={showRejectModal}
        onClose={() => setShowRejectModal(false)}
        title="Reject Vendor"
      >
        <div className="space-y-4">
          <p>Please provide a reason for rejecting this vendor:</p>
          <TextArea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Enter rejection reason..."
            rows={3}
          />
          <div className="flex gap-3">
            <Button
              variant="secondary"
              onClick={() => setShowRejectModal(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                onReject(rejectReason);
                setShowRejectModal(false);
                setRejectReason('');
              }}
              disabled={!rejectReason.trim()}
              className="flex-1"
            >
              Reject Vendor
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

// Vendor Management Table Component
const VendorManagementTable: React.FC<{
  vendors: Plumber[];
  onApprove: (vendorId: string) => void;
  onReject: (vendorId: string, reason: string) => void;
  onSetPreferred: (vendorId: string) => void;
}> = ({ vendors, onApprove, onReject, onSetPreferred }) => {
  return (
    <Card className="overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Vendor
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Category
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Rating
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {vendors.map((vendor) => (
              <tr key={vendor.id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-[#E3F2FD] rounded-full flex items-center justify-center mr-3">
                      <WrenchIcon className="text-[#1E88E5]" size={16} />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-900">{vendor.name}</div>
                      <div className="text-sm text-gray-500">{vendor.location}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="text-sm text-gray-900 capitalize">{vendor.category}</span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <StarIcon size={16} className="text-yellow-400 mr-1" />
                    <span className="text-sm text-gray-900">{vendor.rating}</span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <StatusBadge status={vendor.hoaApprovalStatus || 'pending'} />
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex gap-2">
                    {vendor.hoaApprovalStatus === 'pending' && (
                      <>
                        <button
                          type="button"
                          onClick={() => onApprove(vendor.id)}
                          className="text-green-600 hover:text-green-900"
                        >
                          Approve
                        </button>
                        <button
                          type="button"
                          onClick={() => onReject(vendor.id, 'General rejection')}
                          className="text-red-600 hover:text-red-900"
                        >
                          Reject
                        </button>
                      </>
                    )}
                    {vendor.hoaApprovalStatus === 'approved' &&
                      vendor.contractType !== 'preferred' && (
                        <button
                          type="button"
                          onClick={() => onSetPreferred(vendor.id)}
                          className="text-purple-600 hover:text-purple-900"
                        >
                          Set Preferred
                        </button>
                      )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
};

// Main App Component
const AppContent: React.FC = () => {
  const ezhoaUser = useAppSelector((state) => state.clerkAuth?.ezhoaUser);
  const { user, isLoaded } = useUser();

  // Remove debug logging in production

  const getDashboard = () => {
    // If Clerk user exists but ezhoaUser hasn't synced yet, show loading
    if (isLoaded && user && !ezhoaUser) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <LoadingSpinner size="lg" />
            <p className="mt-4 text-gray-600">Setting up your dashboard...</p>
          </div>
        </div>
      );
    }

    // If no user at all, show loading (shouldn't happen with SignedIn wrapper)
    if (!ezhoaUser) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <LoadingSpinner size="lg" />
        </div>
      );
    }

    // Lazy-loaded dashboards with Suspense
    switch (ezhoaUser.role) {
      case 'admin':
        return (
          <Suspense
            fallback={
              <div className="flex items-center justify-center min-h-[400px]">
                <LoadingSpinner />
              </div>
            }
          >
            <JaredAdminDashboard />
          </Suspense>
        );
      case 'vendor':
        return (
          <Suspense
            fallback={
              <div className="flex items-center justify-center min-h-[400px]">
                <LoadingSpinner />
              </div>
            }
          >
            <JaredVendorDashboard />
          </Suspense>
        );
      default:
        return (
          <Suspense
            fallback={
              <div className="flex items-center justify-center min-h-[400px]">
                <LoadingSpinner />
              </div>
            }
          >
            <JaredHomeownerDashboard />
          </Suspense>
        );
    }
  };

  return (
    <>
      <ClerkLoading>
        <div className="min-h-screen bg-gradient-to-br from-[#E3F2FD] via-[#BBDEFB] to-[#90CAF9] flex items-center justify-center">
          <LoadingSpinner size="lg" />
        </div>
      </ClerkLoading>
      <ClerkLoaded>
        <Routes>
          {/* Public routes for authentication */}
          <Route
            path="/sign-in/*"
            element={
              <SignedOut>
                <Suspense
                  fallback={
                    <div className="flex items-center justify-center min-h-screen">
                      <LoadingSpinner size="lg" />
                    </div>
                  }
                >
                  <ClerkLogin />
                </Suspense>
              </SignedOut>
            }
          />
          <Route
            path="/sign-up/*"
            element={
              <SignedOut>
                <Suspense
                  fallback={
                    <div className="flex items-center justify-center min-h-screen">
                      <LoadingSpinner size="lg" />
                    </div>
                  }
                >
                  <ClerkSignUp />
                </Suspense>
              </SignedOut>
            }
          />

          {/* Redirect root to dashboard if signed in, otherwise to sign-in */}
          <Route
            path="/"
            element={
              <>
                <SignedIn>
                  <Navigate to="/dashboard" replace />
                </SignedIn>
                <SignedOut>
                  <RedirectToSignIn />
                </SignedOut>
              </>
            }
          />

          {/* Protected routes */}
          <Route
            path="/*"
            element={
              <>
                <SignedIn>
                  <Routes>
                    <Route path="/dashboard" element={<Layout>{getDashboard()}</Layout>} />
                    <Route
                      path="/requests"
                      element={
                        <Layout>
                          <RequestsPage />
                        </Layout>
                      }
                    />
                    <Route
                      path="/vendors"
                      element={
                        <Layout>
                          <VendorsPage />
                        </Layout>
                      }
                    />
                    <Route
                      path="/jobs"
                      element={
                        <Layout>
                          <JobsPage />
                        </Layout>
                      }
                    />
                    <Route
                      path="/messages"
                      element={
                        <Layout>
                          <MessagesPage />
                        </Layout>
                      }
                    />
                    <Route
                      path="/notifications"
                      element={
                        <Layout>
                          <NotificationsPage />
                        </Layout>
                      }
                    />
                    <Route
                      path="/documents"
                      element={
                        <Layout>
                          <Suspense
                            fallback={
                              <div className="flex items-center justify-center min-h-[400px]">
                                <LoadingSpinner />
                              </div>
                            }
                          >
                            <DocumentsPage />
                          </Suspense>
                        </Layout>
                      }
                    />
                    <Route
                      path="/calendar"
                      element={
                        <Layout>
                          <Suspense
                            fallback={
                              <div className="flex items-center justify-center min-h-[400px]">
                                <LoadingSpinner />
                              </div>
                            }
                          >
                            <CalendarPage />
                          </Suspense>
                        </Layout>
                      }
                    />
                    <Route
                      path="/settings"
                      element={
                        <Layout>
                          <SettingsPage />
                        </Layout>
                      }
                    />
                    <Route
                      path="/profile"
                      element={
                        <Layout>
                          <ProfilePage />
                        </Layout>
                      }
                    />
                    <Route
                      path="/ai-agent"
                      element={
                        <Layout>
                          <Suspense
                            fallback={
                              <div className="flex items-center justify-center min-h-[400px]">
                                <LoadingSpinner />
                              </div>
                            }
                          >
                            <JaredAIAgent />
                          </Suspense>
                        </Layout>
                      }
                    />
                    <Route
                      path="/vendor-marketplace"
                      element={
                        <Layout>
                          <Suspense
                            fallback={
                              <div className="flex items-center justify-center min-h-[400px]">
                                <LoadingSpinner />
                              </div>
                            }
                          >
                            <JaredVendorMarketplace />
                          </Suspense>
                        </Layout>
                      }
                    />
                    <Route
                      path="/ai-assistant"
                      element={
                        <Layout>
                          <Suspense
                            fallback={
                              <div className="flex items-center justify-center min-h-[400px]">
                                <LoadingSpinner />
                              </div>
                            }
                          >
                            <JaredAIAgent />
                          </Suspense>
                        </Layout>
                      }
                    />
                    <Route
                      path="/payments"
                      element={
                        <Layout>
                          <Suspense
                            fallback={
                              <div className="flex items-center justify-center min-h-[400px]">
                                <LoadingSpinner />
                              </div>
                            }
                          >
                            <JaredPaymentPage />
                          </Suspense>
                        </Layout>
                      }
                    />
                    <Route
                      path="/broadcasts"
                      element={
                        <Layout>
                          <Suspense
                            fallback={
                              <div className="flex items-center justify-center min-h-[400px]">
                                <LoadingSpinner />
                              </div>
                            }
                          >
                            <JaredCommunityBroadcast />
                          </Suspense>
                        </Layout>
                      }
                    />
                    <Route
                      path="/vendor-assignment"
                      element={
                        <Layout>
                          <Suspense
                            fallback={
                              <div className="flex items-center justify-center min-h-[400px]">
                                <LoadingSpinner />
                              </div>
                            }
                          >
                            <JaredVendorAssignment />
                          </Suspense>
                        </Layout>
                      }
                    />
                    <Route
                      path="/privacy"
                      element={
                        <Layout>
                          <Suspense
                            fallback={
                              <div className="flex items-center justify-center min-h-[400px]">
                                <LoadingSpinner />
                              </div>
                            }
                          >
                            <JaredPrivacySettings />
                          </Suspense>
                        </Layout>
                      }
                    />
                    <Route
                      path="/stripe-demo"
                      element={
                        <Layout>
                          <Suspense
                            fallback={
                              <div className="flex items-center justify-center min-h-[400px]">
                                <LoadingSpinner />
                              </div>
                            }
                          >
                            <StripeDemoPage />
                          </Suspense>
                        </Layout>
                      }
                    />
                    {/* Catch-all route */}
                    <Route path="/*" element={<Navigate to="/dashboard" replace />} />
                  </Routes>
                </SignedIn>
                <SignedOut>
                  <RedirectToSignIn />
                </SignedOut>
              </>
            }
          />
        </Routes>

        {/* Modals - only show when signed in */}
        <SignedIn>
          <CreateRequestModal />
          <SubmitBidModal />
        </SignedIn>
      </ClerkLoaded>
    </>
  );
};

export default function App() {
  // Temporarily disable PersistGate to fix loading issue
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    // Clear any corrupted persist data
    if (typeof window !== 'undefined') {
      try {
        const persistRoot = localStorage.getItem('persist:root');
        if (persistRoot) {
          try {
            JSON.parse(persistRoot);
          } catch (_e) {
            console.error('Corrupted persist data, clearing...');
            localStorage.removeItem('persist:root');
          }
        }
      } catch (e) {
        console.error('LocalStorage error:', e);
      }
    }

    // Mark as hydrated after a short delay
    setTimeout(() => setIsHydrated(true), 100);
  }, []);

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#E3F2FD] via-[#BBDEFB] to-[#90CAF9] flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <Provider store={store}>
        <ClerkAuthSync>
          <BrowserRouter>
            <AppContent />
            <ToastContainer />
          </BrowserRouter>
        </ClerkAuthSync>
      </Provider>
    </ErrorBoundary>
  );
}
